import { useState, useEffect, FC } from 'react';
import { formatNumber, formatNumberAsCurrency } from 'helpers/utils';
import { CustomDonationSpan, DonateAmountButtonsContainer, InputCustomDonation } from '../styled';
import classNames from 'classnames';
import { OathRecurringInterval } from 'store/pay/types';
import { getDisplayTextFromRecurringInterval } from 'screens/Checkout/Form/utils';
import { AUTOPILOT_CLIENT_ID } from 'helpers/donate';

interface Props {
  children: React.ReactNode;
  containerClassName?: string;
  donationAmounts: number[];
  isMonthlyEnabled?: boolean;
  isRecurring?: boolean;
  isRecurringDonationValid?: boolean;
  numRecipients?: number;
  clientId?: number;
  selectedDonationAmount?: number;
  setIsRecurring?: (isRecurring: boolean) => void;
  recurringInterval?: OathRecurringInterval;
  setSelectedDonationAmount: (amount?: number) => void;
  fromIssues?: boolean;
  label?: string;
  donationButtonsSingleRowDesktop?: boolean;
}

export const DonationTotal: FC<Props> = ({
  children,
  containerClassName,
  donationAmounts,
  isMonthlyEnabled,
  isRecurring,
  isRecurringDonationValid,
  numRecipients,
  clientId,
  selectedDonationAmount,
  setIsRecurring,
  recurringInterval = 'month',
  setSelectedDonationAmount,
  fromIssues = false,
  label,
  donationButtonsSingleRowDesktop,
}) => {
  const [customDonateAmount, setCustomDonateAmount] = useState<number | undefined>();
  const [isDonationInputVisible, setIsDonationInputVisible] = useState(false);
  const isAutopilotClient = clientId === AUTOPILOT_CLIENT_ID;

  useEffect(() => {
    if (isDonationInputVisible) {
      document.getElementById('custom-donate-input')?.focus();
    }
  }, [isDonationInputVisible]);

  useEffect(() => {
    if (selectedDonationAmount && !donationAmounts.includes(selectedDonationAmount)) {
      setCustomDonateAmount(selectedDonationAmount);
    }
  }, [selectedDonationAmount]);

  // For Autopilot client, default donations to recurring
  useEffect(() => {
    if (isAutopilotClient && isMonthlyEnabled && setIsRecurring) {
      setIsRecurring(true);
    }
  }, [isAutopilotClient, isMonthlyEnabled, setIsRecurring]);

  const getOnDonateAmountClick = (amount?: number) => () => {
    setSelectedDonationAmount(amount);
  };

  const getIsSelected = (amount?: number) => {
    return !!selectedDonationAmount && amount === selectedDonationAmount;
  };

  const hideDonationInput = () => {
    setIsDonationInputVisible(false);
  };

  const toggleDonationInput = () => {
    setIsDonationInputVisible(isVisible => !isVisible);
  };

  const onCustomDonationClick = () => {
    toggleDonationInput();
    if (customDonateAmount) {
      setSelectedDonationAmount(customDonateAmount);
    }
  };

  const onDonationInputChange = e => {
    if (e.target.value) {
      setCustomDonateAmount(Math.round(Number.parseFloat(e.target.value) * 100) / 100);
      return;
    }
    setCustomDonateAmount(undefined);
  };

  const onInputBlur = event => {
    if (event.relatedTarget?.id !== 'donate-amount-button') {
      if (customDonateAmount) {
        setSelectedDonationAmount(customDonateAmount);
      }
      hideDonationInput();
    }
  };

  const getCustomDonationDisplay = () =>
    customDonateAmount ? formatNumber(customDonateAmount) : null;

  return (
    <div
      className={classNames('donation-total-container', containerClassName, {
        'from-issues': fromIssues,
        'donate-page': !fromIssues,
      })}
    >
      {isMonthlyEnabled && setIsRecurring && (
        <div className="display-flex">
          <div className="recurring-toggle-group">
            <label
              className={classNames('radio-tab', {
                disabled: isAutopilotClient,
              })}
            >
              <input
                type="radio"
                name="recurring_interval"
                value=""
                checked={!isRecurring}
                onClick={() => setIsRecurring(false)}
                disabled={isAutopilotClient}
              />
              <div className="toggle-option">
                <span className="body-small-text color-inherit">Give Once</span>
              </div>
            </label>
            <label className="radio-tab">
              <input
                type="radio"
                name="recurring_interval"
                value="recurring"
                checked={isRecurring}
                onClick={() => setIsRecurring(true)}
              />
              <div className="toggle-option">
                <span className="body-small-text color-inherit">
                  Give {getDisplayTextFromRecurringInterval(recurringInterval, true)}
                </span>
              </div>
            </label>
          </div>
        </div>
      )}
      {isRecurring && !isRecurringDonationValid && (
        <div className="text-center mb14">
          <span className="utility-text color-oathBlue">{`Recurring monthly donations must be evenly split, for all ${numRecipients} recipients. Customized splits are only allowed for one-time donations. Select all recipients to set up recurring monthly donations.`}</span>
        </div>
      )}
      <div
        className={classNames('eyebrow-text color-inkBlue', { 'text-center': isMonthlyEnabled })}
      >
        {label || 'Select contribution amount:'}
      </div>
      <DonateAmountButtonsContainer $singleRowDesktop={donationButtonsSingleRowDesktop}>
        {donationAmounts.map((amount, index) => (
          <button
            key={`${amount}-${index}`}
            type="button"
            className={classNames('donate-amount-button', {
              selected: getIsSelected(amount),
              'single-row': donationButtonsSingleRowDesktop,
            })}
            onClick={getOnDonateAmountClick(amount)}
          >
            {formatNumberAsCurrency(amount)}
          </button>
        ))}
        <button
          id="donate-amount-button"
          type="button"
          className={classNames('donate-amount-button', {
            selected: getIsSelected(customDonateAmount),
            'single-row': donationButtonsSingleRowDesktop,
          })}
          onClick={onCustomDonationClick}
        >
          $
          {!isDonationInputVisible && (
            <CustomDonationSpan $isSelected={getIsSelected(customDonateAmount)}>
              {getCustomDonationDisplay()}
            </CustomDonationSpan>
          )}
          {isDonationInputVisible && (
            <InputCustomDonation
              type="number"
              id="custom-donate-input"
              value={customDonateAmount || ''}
              onChange={onDonationInputChange}
              onClick={e => {
                e.stopPropagation();
              }}
              onBlur={onInputBlur}
            />
          )}
        </button>
      </DonateAmountButtonsContainer>
      {children}
    </div>
  );
};
